/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useState } from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import styled, { css } from 'styled-components';
import { graphql, useStaticQuery } from 'gatsby';
import { useIntl } from 'gatsby-plugin-intl';

import { ModalType } from '../state/ModalProvider';
import { isRegionLocked } from '../utils/regionLockedContent';
import { useProvince } from '../state/ProvinceProvider';
import theme from '../themes/phoenix/theme';
import { fixPropWarnings } from '../utils/fixPropWarnings';

import Image from "./Image";
import { Modal } from './Modal';
import RichText from './RichTextV2';
import { Body3, Heading3, Heading5 } from './Typography';
import ButtonOrLink from './ButtonOrLink';
import CallToAction from './CallToAction';
import InlineLink from './InlineLink';

const Step1Heading = styled(Heading3)`
  margin-bottom: 10px;
`;

const Step2Heading = styled(Heading5)`
  margin-top: 10px;
`;

const step1Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_: any, children: any) => <Step1Heading>{children}</Step1Heading>,
    [BLOCKS.HEADING_2]: (_: any, children: any) => <Heading3>{children}</Heading3>,
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => <Heading5>{children}</Heading5>,
  },
};

const step2Options = {
  renderNode: {
    [BLOCKS.HEADING_5]: (_: any, children: any) => <Step2Heading>{children}</Step2Heading>,
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => <Body3>{children}</Body3>,
  },
};

const footerOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => <Body3>{children}</Body3>,
  },
};

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 20px;
  row-gap: 10px;
`;

const FullWidthLogoBox = styled(ButtonOrLink)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  margin-bottom: 20px;
  padding: 8px 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const HalfWidthLogoBox = styled(FullWidthLogoBox)`
  width: calc(50% - 10px);
`;

const LogoImage = styled(Image)`
  height: auto;
  width: auto;
  max-width: 100%;
`;

const RadioButton = styled.label.withConfig(fixPropWarnings('checked'))<{checked: boolean}>`
  display: flex;
  width: 100%;
  align-items: center;
  text-align: left;
  background-color: #f9f9f9;
  padding: 12px 20px;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  border: 1px solid transparent;
  border-color: ${(props) => (props.checked ? '#ccc' : 'none')};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    border-color: #ccc;
  }
  p{
    margin: 0px;
  }

  input {
    margin-right: 5px;
     opacity: 0;
  }

  &::before {
    content: "";
    width: 15px;
    height: 15px;
    display: inline-block;
    flex: 0 0 auto;
    border-radius: 50%;
    border: 1px solid white;
    box-shadow: 0 0 0 1px ${theme.colors.black};
    transition: background-color 100ms ease;

    ${({ checked }) => checked && css`
      background-color: ${theme.colors.black};
    `}

  }
`;

const ContinueButton = styled(CallToAction)`
${(props) => props.disabled
    && css`
        background-color: ${theme.colors.greys[5]};
        border-color: ${theme.colors.greys[5]};
        color: ${theme.colors.whites[0]};
      `}
      margin: 0 auto;
`;

const InlineLinkWrapper = styled.div`
  margin-top: 20px;
`;

const SelfServeModal = () => {
  const { activeProvince } = useProvince();
  const [step, setStep] = useState(0);
  const [urlSelected, setUrlSelected] = useState<string|undefined>();
  const { locale } = useIntl();
  const { en, fr } = useStaticQuery(query);
  const modalData = locale === 'fr' ? fr : en;
  if (!modalData) return null;
  const { modalContentSections } = modalData;

  const onPolicyTypeClick = (url: string) => {
    if (url === urlSelected) {
      setUrlSelected(undefined);
    } else setUrlSelected(url);
  };
  const onContinueClick = () => {
    if (urlSelected) window.open(urlSelected, '_blank');
  };

  const handleNextPressed = () => setStep(step + 1);
  const handleBackPressed = () => setStep(step - 1);
  const handleClose = () => setStep(0);

  const customStyles = {
    display: 'flex',
    flexDirection: 'column' as const,
    color: theme.colors.black,
    '> :last-child': {
      marginBottom: 0,
    },
  };

  // filter region-locked content
  const filterLinks = (links: any[]) => links.filter((node: any) => {
    if (node?.internal?.type === 'ContentfulRegionLockedContent') {
      return !isRegionLocked({ ...node, province: activeProvince });
    }
    return true;
  });

  const renderLogoComponent = (node: any, i: number) => {
    const LogoComponent = i === 0 ? FullWidthLogoBox : HalfWidthLogoBox;
    const isClickable = i === 0;

    switch (node?.internal?.type) {
      case 'ContentfulLinkWrapper':
        return (
          <LogoComponent
            key={node.id}
            {...(isClickable
              ? { onClick: handleNextPressed, dataTestId: 'logoButton' }
              : { href: node.linkUrl, dataTestId: 'logoLink' })}
          >
            <LogoImage data={node.iconDamAsset} key={node.id} />
          </LogoComponent>
        );
      case 'ContentfulRegionLockedContent': {
        const linkWrapper = node?.content?.find(
          (content: any) => content?.internal?.type === 'ContentfulLinkWrapper',
        );
        return (
          <LogoComponent key={node.id} dataTestId="logoLink">
            <LogoImage data={linkWrapper.iconDamAsset} key={node.id} />
          </LogoComponent>
        );
      }
      default:
        return null;
    }
  };

  const renderCombinatorSectionItems = (items: any[]) => filterLinks(items)
    .map((node: any, i: number) => renderLogoComponent(node, i));

  const renderRadioOption = (
    node: any,
    onOptionClick: (url: string) => void,
    checked: boolean,
  ) => (
    <RadioButton key={node?.id} checked={checked}>
      <input
        type="radio"
        name="policy"
        onClick={() => onOptionClick(node?.fieldValues?.[0])}
        checked={checked}
      />
      <Body3>{node.displayLabel}</Body3>
    </RadioButton>
  );

  const renderFormFields = (items: any[]) => items.map((node: any) => {
    switch (node.contentful_id) {
      case '62a9YHKpSHgauaYw2DmqEG':
      case '5OuF41dwfDGXE118tQ0Pqs':
        return renderRadioOption(
          node,
          onPolicyTypeClick,
          urlSelected === node?.fieldValues?.[0],
        );
      case '50Lg1CEixxzNpJmSEmvR3j':
        return (
          <ContinueButton
            key={node?.contentful_id}
            disabled={urlSelected === undefined}
            onClick={onContinueClick}
            variant="tertiary-btn"
          >
            {node.displayLabel}
          </ContinueButton>
        );
      default:
        return null;
    }
  });

  const renderModalContent = () => modalContentSections?.map((modalStep: any, index: number) => {
    switch (modalStep?.contentful_id) {
      // Step 1 Select Provider
      case '1KCkrgtPToQ1n75gEgImLo':
        return index === step && (
        <Fragment key={modalStep?.contentful_id}>
          <RichText document={modalStep?.sectionTitle} options={step1Options} />
          <RichText document={modalStep?.sectionSubtitle} options={step1Options} />
          {modalStep?.combinatorSectionItems?.map((item: any) => {
            switch (item?.internal?.type) {
              case 'ContentfulLinkSection':
                return (
                  <Container key={item.id}>
                    {renderCombinatorSectionItems(item.links)}
                  </Container>
                );
              case 'ContentfulTextSection':
                return <RichText key={item?.id} document={item.bodyRTF} options={footerOptions} />;
              default:
                return null;
            }
          })}
        </Fragment>
        );
      // Step 2 Select policy type
      case '6r4k11U5Redc1eFNlbBR7m':
        return index === step && (
          <Fragment key={modalStep?.contentful_id}>
            <RichText document={modalStep?.sectionTitle} options={step2Options} />
            {modalStep?.combinatorSectionItems?.map((item: any) => {
              switch (item?.internal?.type) {
                case 'ContentfulForm':
                  return (
                    <Container key={item.id}>
                      {renderFormFields(item.formFields)}
                    </Container>
                  );
                case 'ContentfulTextSection':
                  return (
                    <Fragment key={item?.id}>
                      <RichText document={item?.headingRTF} options={step2Options} />
                      <RichText document={item?.bodyRTF} options={step2Options} />
                    </Fragment>
                  );
                case 'ContentfulLinkWrapper':
                  return (
                    <InlineLinkWrapper key={item.id}>
                      <InlineLink>
                        {item.linkText}
                      </InlineLink>
                    </InlineLinkWrapper>
                  );
                default:
                  return null;
              }
            })}
          </Fragment>
        );
      default:
        return null;
    }
  });

  return (
    <Modal
      type={ModalType.SELF_SERVE}
      showBack={step > 0}
      onBack={handleBackPressed}
      onClose={handleClose}
      style={customStyles}
    >
      {renderModalContent()}
    </Modal>
  );
};

const query = graphql`
  query {
    en: contentfulModal(
      modalType: { eq: "SELF_SERVE" }
      node_locale: { eq: "en-CA" }
    ) {
      ...Modal  
    }
    fr: contentfulModal(
      modalType: { eq: "SELF_SERVE" }
      node_locale: { eq: "fr-CA" }
    ) {
      ...Modal  
    }
  }
`;

export default SelfServeModal;
